import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Availability, findAvailSimilarTo, getAvailabilityLabel, parseAvailability } from "@caredna-marlin/utils/client/availability";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import ProviderSelectTable from './ProviderSelectTable';
import { AvailabilityByTimespanQuery, useAvailabilityByTimespanQuery } from '~/graphql/__generated__/graphql';
import { startOfToday } from 'date-fns';
import { Dictionary, flatten, groupBy } from 'lodash';
import Help from '~/components/common/Help';
import { AvailabilityAction, MulipleAvailabilityAction } from '~/hooks/useAvailabilityOperation';
import { unknownType } from '~/hooks/visitDragDropUtils';

type Props = {
  action: AvailabilityAction | MulipleAvailabilityAction,
  availability: Availability,
  additionalProviders: Record<string, boolean>,
  expanded: boolean,
  setAdditionalProviders: (additional: Record<string, boolean>) => void,
  setExpanded?: (expanded: boolean) => void,
  setAdditionalAvailabilityIds: (additional: number[]) => void,
}

const useExcludeProviders = (
  action: AvailabilityAction | MulipleAvailabilityAction,
  availability: Availability,
  similarByProvider: Dictionary<Availability[]>,
  providers: AvailabilityByTimespanQuery["provider"]
) => {
  switch (action) {
    case "template":
    case "create":
      // While creating or using a provider as a template, exclude the provider we're already working with.
      return [availability.providerId];

    case "edit":
    case "delete":
      // while editing or deleting, exclude all providers that *don't* have a similar availability
      return providers.filter(p => !similarByProvider[p.id] || p.id === availability.providerId).map(p => p.id);
    case "copy":
      // while copying, don't exclude any providers, even the current one; you can edit and then copy an avail onto
      // the same person.
      return []
    default: return unknownType(action)
  }
}

const BulkEditCopy = ({ action, availability, additionalProviders, expanded, setAdditionalAvailabilityIds, setAdditionalProviders, setExpanded }: Props) => {
  const { data } = useAvailabilityByTimespanQuery({ variables: {
    start: startOfToday().toISOString(),
  }});

  let similarAvailability: Availability[] = [];
  if (data && (action === "edit" || action === "delete")) {
    similarAvailability = findAvailSimilarTo(availability, data?.availability.map(a => parseAvailability(a)));
  }

  const similarByProvider = groupBy(similarAvailability, "providerId");
  const excludeProviders = useExcludeProviders(action, availability, similarByProvider, data?.provider || []);

  const setSelectedProviders = (additionalProviders: Record<string, boolean>) => {
    setAdditionalProviders(additionalProviders);

    const providerIdsSelected = Object.keys(additionalProviders).filter(p => additionalProviders[p]);
    const similarAvailThatShouldBeUpdated = flatten(providerIdsSelected.map(p => similarByProvider[p]?.map(a => a.id)));
    setAdditionalAvailabilityIds(similarAvailThatShouldBeUpdated);
  };

  if (!data?.provider.length || excludeProviders.length === data?.provider.length) {
    return null;
  }

  return <Accordion expanded={expanded} onChange={() => setExpanded?.(!expanded)}>
    <Summary
      action={action}
      availability={availability}
      selectedCount={Object.values(additionalProviders).filter(Boolean).length}
    />
    <AccordionDetails>
      <ProviderSelectTable
        data={data}
        excludeProviders={excludeProviders}
        selectedProviders={additionalProviders}
        setSelectedProviders={setSelectedProviders}
        highlightAvailability={similarAvailability.map(a => a.id)}
      />
    </AccordionDetails>
  </Accordion>
}

export default BulkEditCopy;

const Summary = ({ action, availability, selectedCount }: { action: AvailabilityAction | MulipleAvailabilityAction, availability: Availability, selectedCount: number }) => {
  switch (action) {
    case "create": return <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      Create for additional providers {selectedCount ? ` - ${selectedCount} selected` : ""}
    </AccordionSummary>;

    case "edit": return <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      Edit for additional providers {selectedCount ? ` - ${selectedCount} selected` : ""}
      <Help text={`Other providers also have this ${getAvailabilityLabel(availability).toLowerCase()}; apply your changes to them as well.`} />
    </AccordionSummary>;

    case "copy": return <AccordionSummary sx={{ m: 0 }} slotProps={{ content: { sx: { m: 0 }}}}>
      Copy to which providers - {selectedCount} selected
      {!selectedCount && <Typography color="error" component="span"> *</Typography>}
      <Help text={`This will not edit the original ${getAvailabilityLabel(availability).toLowerCase()}.`} />
    </AccordionSummary>;

    case "delete": return <AccordionSummary sx={{ m: 0 }} slotProps={{ content: { sx: { m: 0 }}}}>
      Delete for additional providers {selectedCount ? ` - ${selectedCount} selected` : ""}
      <Help text={`Other providers also have this ${getAvailabilityLabel(availability).toLowerCase()}; delete it for them as well.`} />
    </AccordionSummary>;

    case "template": return null

    default: return unknownType(action);
  }
}
